import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  CreditCard,
  Zap,
  Leaf,
  Smartphone,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  Shield,
  TrendingUp,
  Users } from
'lucide-react';
import { mockData } from '../mock/landingMock';
import ContactFormModal from '../components/ContactFormModal';

const LandingPage = () => {
  const [selectedCard, setSelectedCard] = useState('nfc');
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [contactButtonType, setContactButtonType] = useState('general');

  const openContactModal = (type) => {
    setContactButtonType(type);
    setIsContactModalOpen(true);
  };

  return (
    <div className="landing-page">
      <ContactFormModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        buttonType={contactButtonType} />
      
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-slate-700 rounded-lg flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-900">{mockData.brandName}</span>
          </div>
          <nav className="hidden md:flex items-center space-x-8">
            <a href="#features" className="text-slate-600 hover:text-blue-600 transition-colors">Features</a>
            <a href="#how-it-works" className="text-slate-600 hover:text-blue-600 transition-colors">How it Works</a>
            <a href="#products" className="text-slate-600 hover:text-blue-600 transition-colors">Products</a>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white">
              Get Started
            </Button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100"></div>
          <img
            src="https://images.unsplash.com/photo-1524168948265-8f79ad8d4e33"
            alt="Background"
            className="w-full h-full object-cover opacity-10" />
          
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center space-x-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium">
                <Sparkles className="w-4 h-4" />
                <span>Next Generation Business Cards</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold text-slate-900 leading-tight">
                {mockData.hero.headline}
              </h1>
              
              <p className="text-xl text-slate-600 leading-relaxed">
                {mockData.hero.subheadline}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 items-center">
                <Button 
                  size="lg" 
                  className="bg-blue-600 hover:bg-blue-700 text-white group"
                  onClick={() => openContactModal('sales')}
                  data-testid="hero-order-card-btn"
                >
                  Order Your Card
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
                <span className="text-lg font-bold text-blue-700" data-testid="hero-discount-text">
                  Now Super Discount -30 % only 1100 PHP!
                </span>
              </div>

              <div className="flex items-center space-x-8 pt-4">
                <div>
                  <div className="text-3xl font-bold text-slate-900">10K+</div>
                  <div className="text-sm text-slate-600">Happy Customers</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-slate-900">99.9%</div>
                  <div className="text-sm text-slate-600">Uptime</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-slate-900">4.9★</div>
                  <div className="text-sm text-slate-600">Rating</div>
                </div>
              </div>
            </div>

            <div className="relative">
              {/* Main hero video - made bigger */}
              <div className="relative z-10">
                <video
                  src="https://customer-assets-7cd3h4nn.emergentagent.net/job_tap-card-hub/artifacts/dy5g170l_TapcardPro%20web2%20video.mp4"
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="rounded-2xl shadow-2xl w-full h-auto"
                  alt="TapCard Pro Demo Video" />
                
              </div>
              
              <div className="absolute -bottom-6 -left-6 w-72 h-72 bg-blue-200 rounded-full blur-3xl opacity-30"></div>
              <div className="absolute -top-6 -right-6 w-72 h-72 bg-slate-200 rounded-full blur-3xl opacity-30"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Why Choose Digital Business Cards?
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Transform how you network with cutting-edge NFC technology
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {mockData.features.map((feature, index) =>
            <Card key={index} className="border-slate-200 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                <CardHeader>
                  <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${feature.color} flex items-center justify-center mb-4`}>
                    {feature.icon === 'zap' && <Zap className="w-6 h-6 text-white" />}
                    {feature.icon === 'leaf' && <Leaf className="w-6 h-6 text-white" />}
                    {feature.icon === 'smartphone' && <Smartphone className="w-6 h-6 text-white" />}
                    {feature.icon === 'shield' && <Shield className="w-6 h-6 text-white" />}
                  </div>
                  <CardTitle className="text-xl text-slate-900">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-slate-600">
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              How It Works
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Get started in three simple steps
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {mockData.howItWorks.map((step, index) =>
            <div key={index} className="relative">
                <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-slate-700 rounded-full flex items-center justify-center text-white text-2xl font-bold mb-6">
                    {step.step}
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">{step.title}</h3>
                  <p className="text-slate-600">{step.description}</p>
                </div>
                {index < mockData.howItWorks.length - 1 &&
              <ArrowRight className="hidden md:block absolute top-1/2 -right-4 w-8 h-8 text-slate-300 transform -translate-y-1/2" />
              }
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Our Products
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">Premium quality cards for every need. Scan the QR-code below to see the example of the card. The card includes NFC chip but we can print also QR-code for it.

            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-6">
              <a 
                href="https://scan.page/p/MTzg3T" 
                target="_blank" 
                rel="noopener noreferrer"
                data-testid="add-my-contact-link"
              >
                <Button 
                  size="lg"
                  className="bg-blue-600 hover:bg-blue-700 text-white group"
                  data-testid="add-my-contact-btn"
                >
                  <Smartphone className="w-5 h-5 mr-2" />
                  Add My Contact
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </a>
              
              <div className="flex flex-col items-center">
                <span className="text-slate-600 font-medium mb-2">Or scan the QR-code!</span>
                <div className="bg-white rounded-xl shadow-lg p-2 border border-slate-200">
                  <img 
                    src="https://customer-assets-7cd3h4nn.emergentagent.net/job_tap-card-hub/artifacts/b7uzf3nz_Marko_Koskelin_Manila_Content-1024.jpeg"
                    alt="Scan QR Code to add contact"
                    className="w-28 h-28 object-contain"
                    data-testid="contact-qr-code"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {mockData.products.map((product, index) =>
            <Card
              key={index}
              className={`border-2 transition-all duration-300 hover:shadow-xl cursor-pointer ${
              selectedCard === product.id ? 'border-blue-600 shadow-lg' : 'border-slate-200'}`
              }
              onClick={() => setSelectedCard(product.id)}>
              
                <CardHeader>
                  <div className="aspect-video bg-gradient-to-br from-slate-100 to-blue-50 rounded-lg mb-4 overflow-hidden flex items-center justify-center p-4 relative">
                    <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-contain" />
                  
                    {/* Add QR code for NFC Business Card */}
                    {product.id === 'nfc' &&
                  <div className="absolute -bottom-6 -right-6 z-10 bg-white rounded-xl shadow-2xl p-3">
                        <img
                      src="https://customer-assets.emergentagent.com/job_tap-card-hub/artifacts/1b5m33m9_Juan_Gonzales-1024.jpeg"
                      alt="Scan QR Code"
                      className="w-32 h-32 object-contain" />
                    
                        <p className="text-xs text-center text-slate-700 mt-2 font-medium">
                          Scan the QR-code<br />to see the sample!
                        </p>
                      </div>
                  }
                  </div>
                  <CardTitle className="text-2xl text-slate-900">{product.name}</CardTitle>
                  <CardDescription className="text-slate-600">
                    {product.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 mb-6">
                    {product.features.map((feature, idx) =>
                  <li key={idx} className="flex items-start">
                        <CheckCircle2 className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-600">{feature}</span>
                      </li>
                  )}
                  </ul>
                  <Button
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={() => openContactModal(product.id === 'nfc' ? 'nfc-order' : 'id-order')}>
                  
                    Order Now - {product.price}
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-slate-700 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.unsplash.com/photo-1598270106705-020a72bae1bf"
            alt="Background"
            className="w-full h-full object-cover" />
          
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Upgrade Your Networking?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Join thousands of professionals who've made the switch to smart business cards
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-white text-blue-600 hover:bg-slate-50"
                onClick={() => openContactModal('getstarted')}>
                
                Get Started Today
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white/10"
                onClick={() => openContactModal('sales')}>
                
                Contact Sales
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-300 py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-slate-700 rounded-lg flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-white" />
                </div>
                <span className="text-lg font-bold text-white">{mockData.brandName}</span>
              </div>
              <p className="text-sm text-slate-400">
                Revolutionizing business networking with smart NFC technology.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Products</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-blue-400 transition-colors">NFC Business Cards</a></li>
                <li><a href="#" className="hover:text-blue-400 transition-colors">ID Cards</a></li>
                <li><a href="#" className="hover:text-blue-400 transition-colors">Custom Printing</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-blue-400 transition-colors">About Us</a></li>
                <li><a href="#" className="hover:text-blue-400 transition-colors">Contact</a></li>
                <li><a href="#" className="hover:text-blue-400 transition-colors">Support</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-blue-400 transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-blue-400 transition-colors">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 text-center text-sm text-slate-400">
            <p>&copy; 2024 {mockData.brandName}. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>);

};

export default LandingPage;